import React from 'react';
import './Button.css';

/**
 * Canonical button primitive.
 *
 * Self-contained: emits only .ui-button* classes that consume tokens.
 * No longer depends on the legacy .role-action-btn / .director-mini-btn
 * rules in RoleDashboards.css. Works on any surface, including pages
 * that do not import RoleDashboards.css (auth, public, future modules).
 *
 * Backward compatibility (Phase 3): every prop that existed before this
 * phase (variant='primary'|'secondary'|'danger', size='md'|'sm', loading,
 * loadingLabel, disabled, type, className) keeps its exact original
 * default and rendered output when the new props below are omitted --
 * verified by the pre-existing test suite, which is unmodified. No
 * legacy prop was removed or renamed.
 *
 * New in Phase 3:
 *   - variant gains 'ghost', 'link', and 'destructive'. 'destructive' is
 *     an alias for 'danger' (identical CSS, shared selector) -- 'danger'
 *     is not deprecated, both render identically, existing 'danger'
 *     consumers are unaffected.
 *   - size gains 'lg', wired to the --btn-*-lg tokens (already defined in
 *     tokens.css but previously unused by any component).
 *   - leadingIcon / trailingIcon: optional ReactNode rendered before/after
 *     the label, each wrapped `aria-hidden` (decorative -- the visible
 *     label text carries the accessible name, exactly like every
 *     icon-only pattern already in this codebase, e.g. DataTable's
 *     expander button).
 *   - iconOnly: renders only the icon content (children, or leadingIcon/
 *     trailingIcon as a fallback), wrapped `aria-hidden`, with equal
 *     x/y padding so the button reads as a compact square. Strongly
 *     enforced accessible name: emits a console.warn in development if
 *     neither aria-label nor aria-labelledby is supplied.
 *   - fullWidth: sets width: 100%.
 *
 * @param {object} props
 * @param {'primary'|'secondary'|'danger'|'destructive'|'ghost'|'link'} [props.variant='primary']
 * @param {'sm'|'md'|'lg'} [props.size='md']
 * @param {boolean} [props.loading=false] - When true, button is disabled and
 *   renders loadingLabel if provided.
 * @param {string} [props.loadingLabel] - Text to show while loading; defaults to children.
 * @param {boolean} [props.disabled=false]
 * @param {'button'|'submit'|'reset'} [props.type='button'] - Default 'button'
 *   (safer than the HTML default of 'submit').
 * @param {boolean} [props.fullWidth=false]
 * @param {React.ReactNode} [props.leadingIcon]
 * @param {React.ReactNode} [props.trailingIcon]
 * @param {boolean} [props.iconOnly=false] - Requires an aria-label or
 *   aria-labelledby prop; warns in development if missing.
 * @param {string} [props.className] - Extra classes appended after the variant classes.
 * @param {React.ReactNode} props.children
 */
const Button = React.forwardRef(function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  loadingLabel,
  disabled = false,
  type = 'button',
  fullWidth = false,
  leadingIcon = null,
  trailingIcon = null,
  iconOnly = false,
  className = '',
  children,
  ...rest
}, ref) {
  const classes = [
    'ui-button',
    `ui-button--${size}`,
    `ui-button--${variant}`,
    fullWidth ? 'ui-button--full-width' : '',
    iconOnly ? 'ui-button--icon-only' : '',
    className,
  ].filter(Boolean).join(' ');

  const isDisabled = disabled || loading;
  const resolvedLabel = loading && loadingLabel !== undefined ? loadingLabel : children;

  if (process.env.NODE_ENV !== 'production' && iconOnly) {
    const hasAccessibleName = Boolean(rest['aria-label'] || rest['aria-labelledby']);
    if (!hasAccessibleName) {
      // eslint-disable-next-line no-console
      console.warn('Button: an iconOnly button must have an aria-label or aria-labelledby so it has an accessible name.');
    }
  }

  const content = iconOnly ? (
    <span className="ui-button__icon" aria-hidden="true">
      {loading && loadingLabel !== undefined ? loadingLabel : (children ?? leadingIcon ?? trailingIcon)}
    </span>
  ) : (
    <>
      {leadingIcon ? <span className="ui-button__icon ui-button__icon--leading" aria-hidden="true">{leadingIcon}</span> : null}
      {resolvedLabel}
      {trailingIcon ? <span className="ui-button__icon ui-button__icon--trailing" aria-hidden="true">{trailingIcon}</span> : null}
    </>
  );

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
});

export default Button;
